import { useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import {
  EntStructMatchingChart,
  type EvaluationData,
  type LigneMatchingEntStruct,
} from "@repo/results";
import { calculerScores, lignesMatchingEntStruct } from "@repo/utils";

type Props = {
  /** Passations ent-struct de la campagne (`undefined` = chargement). */
  evaluations: EvaluationData[] | undefined;
};

/** Nombre de candidats affichés par défaut dans le classement. Au-delà, la
 *  lecture ligne à ligne n'apporte plus rien : le tableau sert à repérer le
 *  haut du classement, pas à parcourir toute la campagne. */
const TOP_CLASSEMENT = 5;

/**
 * Signature du barème d'un candidat : compétences ET poids, triés. Deux
 * candidats ne sont comparables que si elle est identique — un même score global
 * calculé sur deux barèmes différents ne classe rien.
 */
function signatureBareme(lignes: LigneMatchingEntStruct[]): string {
  return lignes
    .map((l) => `${l.competence}:${l.poids}`)
    .sort()
    .join("|");
}

type EntreeClassement = {
  evaluation: EvaluationData;
  /** Moyenne pondérée des taux de couverture, sur 100. `null` si non calculable. */
  score: number | null;
  lignes: LigneMatchingEntStruct[];
  signature: string;
};

/**
 * Matching entretien structuré : classement des candidats sur le score global
 * pondéré, radar de couverture du candidat sélectionné.
 *
 * Le référentiel est ici le BARÈME DU TEMPLATE — les poids par compétence saisis
 * à la conception de l'entretien — et non un référentiel de référents comme en
 * PAMI ou fit-culturel : l'entretien structuré porte déjà sa cible.
 */
export function EntStructMatchingTab({ evaluations }: Props) {
  const [parcoursSelectionne, setParcoursSelectionne] = useState<string | null>(
    null,
  );
  const [toutAfficher, setToutAfficher] = useState(false);

  /**
   * Classement trié sur le score global PONDÉRÉ, décroissant. Les critères non
   * posés restent dans le maximum : `calculerScores` ne compte que les critères
   * `covered`, donc un entretien écourté pèse sur le score — ce qui est
   * l'intention en classement, où l'on compare ce qui a été prouvé.
   *
   * Les candidats sans score exploitable finissent la liste (`-1` est hors de
   * l'intervalle 0-100 atteignable par un score réel).
   */
  const classement = useMemo<EntreeClassement[]>(() => {
    if (!evaluations) return [];

    const entrees = evaluations.map((evaluation) => {
      const scores = evaluation.questionsEtat
        ? calculerScores(evaluation.questionsEtat)
        : null;
      if (!scores || scores.scoreGlobalMax <= 0) {
        return { evaluation, score: null, lignes: [], signature: "" };
      }
      // La compétence neutre, hors barème, est écartée : la signature du
      // template ne dépend pas de sa présence.
      const lignes = lignesMatchingEntStruct(scores);
      return {
        evaluation,
        // `scoreGlobalObtenu / scoreGlobalMax` = Σ(taux × poids) / Σ poids : la
        // pondération est déjà dedans, inutile de la réappliquer.
        score: (scores.scoreGlobalObtenu / scores.scoreGlobalMax) * 100,
        lignes,
        signature: signatureBareme(lignes),
      };
    });

    return entrees.sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
  }, [evaluations]);

  /**
   * Barèmes hétérogènes : deux candidats passés sur des templates différents ne
   * sont pas comparables. On le DIT plutôt que d'afficher un classement qui
   * mélangerait des échelles — un score de 70 % sur cinq compétences et un 70 %
   * sur douze ne disent pas la même chose.
   */
  const baremesHeterogenes = useMemo(() => {
    const signatures = new Set(
      classement.filter((e) => e.score !== null).map((e) => e.signature),
    );
    return signatures.size > 1;
  }, [classement]);

  /**
   * Le tableau n'affiche que le haut du classement. Le candidat sélectionné est
   * toujours joint, même hors du top, pour que la ligne active reste visible
   * au-dessus de son radar.
   */
  const classementAffiche = useMemo(() => {
    if (toutAfficher) return classement;
    const top = classement.slice(0, TOP_CLASSEMENT);
    const selectionnee = classement.find(
      (e) => e.evaluation.parcoursId === parcoursSelectionne,
    );
    return selectionnee && !top.includes(selectionnee)
      ? [...top, selectionnee]
      : top;
  }, [classement, toutAfficher, parcoursSelectionne]);

  const selection =
    classement.find((e) => e.evaluation.parcoursId === parcoursSelectionne) ??
    classement.find((e) => e.score !== null) ??
    classement[0];

  const poidsMax = useMemo(
    () => (selection?.lignes ?? []).reduce((m, l) => Math.max(m, l.poids), 0),
    [selection],
  );

  if (!evaluations) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        Chargement des entretiens…
      </p>
    );
  }

  if (baremesHeterogenes) {
    return (
      <Card>
        <CardContent className="flex items-start gap-3 p-6">
          <AlertTriangle className="text-etat-alerte mt-0.5 h-5 w-5 shrink-0" />
          <div className="space-y-1 text-sm">
            <p className="font-medium">Comparaison impossible</p>
            <p className="text-muted-foreground">
              Les candidats de cette campagne n'ont pas passé le même entretien :
              les compétences ou leurs poids diffèrent d'un barème à l'autre. Un
              classement les mettrait sur une échelle commune qui n'existe pas.
              Les restitutions individuelles restent disponibles dans l'onglet
              Statistiques.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Classement — INTRA-campagne uniquement : le score est une moyenne
          pondérée des taux de couverture du barème de CE template. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Classement des candidats{" "}
            {toutAfficher || classement.length <= TOP_CLASSEMENT
              ? `(${classement.length})`
              : `(${TOP_CLASSEMENT} meilleurs sur ${classement.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {classement.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              Aucun entretien FairView terminé dans cette campagne.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      #
                    </th>
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      Candidat
                    </th>
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      Score global pondéré
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {classementAffiche.map((entree) => {
                    const index = classement.indexOf(entree);
                    const actif =
                      selection?.evaluation.parcoursId ===
                      entree.evaluation.parcoursId;
                    return (
                      <tr
                        key={entree.evaluation._id}
                        onClick={() =>
                          setParcoursSelectionne(entree.evaluation.parcoursId)
                        }
                        // La ligne active porte un violet pâle plutôt qu'un gris :
                        // le radar affiché dessous est celui de CE candidat, la
                        // sélection doit se repérer sans la chercher.
                        className={`cursor-pointer border-b last:border-0 ${
                          actif
                            ? "bg-accent hover:bg-accent"
                            : "hover:bg-muted/40"
                        }`}
                      >
                        <td className="text-muted-foreground px-4 py-2 tabular-nums">
                          {entree.score === null ? "—" : index + 1}
                        </td>
                        <td className="px-4 py-2 font-medium">
                          {entree.evaluation.prenomCandidat}{" "}
                          {entree.evaluation.nomCandidat}
                        </td>
                        <td className="px-4 py-2">
                          {entree.score === null ? (
                            <span className="text-muted-foreground">
                              Entretien non exploitable
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="w-8 tabular-nums">
                                {Math.round(entree.score)}
                              </span>
                              <div className="bg-muted relative h-2 w-24 overflow-hidden rounded-full">
                                <div
                                  className="bg-primary h-full rounded-full"
                                  style={{
                                    width: `${Math.max(1, Math.min(100, entree.score))}%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {classement.length > TOP_CLASSEMENT && (
                <div className="border-t p-3 text-center">
                  <button
                    type="button"
                    onClick={() => setToutAfficher((v) => !v)}
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    {toutAfficher
                      ? `Afficher seulement les ${TOP_CLASSEMENT} meilleurs`
                      : `Afficher les ${classement.length} candidats`}
                  </button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Radar du candidat sélectionné. */}
      {selection && selection.lignes.length > 0 && (
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-base">
              {selection.evaluation.prenomCandidat}{" "}
              {selection.evaluation.nomCandidat}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EntStructMatchingChart lignes={selection.lignes} />
            {/* Légende de l'intensité : la couleur porte le poids, elle ne doit
                jamais être le seul canal — les poids exacts sont aussi dans
                l'infobulle de chaque compétence. */}
            <div className="text-muted-foreground flex flex-wrap items-center justify-center gap-3 text-xs">
              <span>Intensité du fond = poids de la compétence :</span>
              <span className="flex items-center gap-1.5">
                <span
                  className="bg-primary h-3 w-6 rounded-sm"
                  style={{ opacity: 0.08 }}
                />
                faible
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="bg-primary h-3 w-6 rounded-sm"
                  style={{ opacity: 0.55 }}
                />
                {poidsMax > 0
                  ? `le plus lourd du barème (${Math.round(poidsMax * 10) / 10})`
                  : "le plus lourd du barème"}
              </span>
              <span>
                La distance au centre est la couverture, jamais le poids.
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
